// Bundled English fallback used by LanguageContext for first paint / offline mode.
// Keep in sync with server/translations/english.json (the runtime source of truth).
export const en = {
  "common": {
    "selectLanguage": "Select Language",
    "english": "English",
    "hindi": "Hindi",
    "cancel": "Cancel",
    "logout": "Logout",
    "login": "Login",
    "register": "Register",
    "backToHub": "Back to test hub",
    "consentNote": "By registering this child, you confirm that consent has been obtained as per your institution's policy and that assessments will be supervised by trained staff.",
    "serverError": "Error connecting to the server.",
    "sessionError": "Could not establish a secure session. Please try again.",
    "speechNotSupported": "Your browser does not support Speech Recognition. Please type manually.",
    "failedToStart": "Failed to start session on server.",
    "failedToSave": "Failed to save assessment. Please try again.",
    "enterReason": "Please enter a reason before stopping.",
    "sessionNotFound": "Session not found. Please refresh and try again.",
    "loading": "Loading…",
    "pageLoadError": "This page could not be loaded. Please try again later.",
    "version2": " - Version 2",
    "version3": " - Version 3"
  },
  "navbar": {
    "id": "ID",
    "language": "🌐 Language"
  },
  "footer": {
    "copyright": "© 2026 Community Empowerment Lab · Sangian Cognitive Test Interface",
    "terms": "Terms & Conditions",
    "privacy": "Privacy Policy",
    "contact": "Contact Us",
    "help": "Help & Support"
  },
  "breadcrumb": {
    "home": "Home",
    "terms": "Terms & Conditions",
    "privacy": "Privacy Policy"
  },
  "home": {
    "heroPill": "🧠 Kauffman-inspired assessment • For supervised use only",
    "welcome": "Welcome to the",
    "title": "Sangian Test Hub",
    "desc": "Select a test module below to start the assessment.",
    "bullet1": "✔ Seven core games mapped to culturally adapted names",
    "bullet2": "✔ Quick access to Numeracy & Literacy readiness checks",
    "bullet3": "✔ Designed for psychologists, teachers & researchers",
    "startTest": "Start Assessment",
    "registerChild": "Register a Child",
    "warningNote": "This digital interface supports structured KABC-style assessments. Always follow your institutional protocol and training.",
    "modulesHeader": "Test Modules",
    "modulesSub": "Tap a card to see what the test measures.",
    "modulesBadge": "modules",
    "lastPlayed": "Last Played:",
    "totalSessions": "Total Sessions:",
    "never": "Never",
    "games": {
      "bagiya": {
        "title": "Bagiya",
        "local": "",
        "tag": "",
        "desc": "Child looks at a set of fun creatures in a 'garden' and later tries to remember where each one was placed.",
        "sub": ""
      },
      "lottery": {
        "title": "Lottery Ka Ticket",
        "local": "",
        "tag": "",
        "desc": "Child listens to a series of numbers and repeats them in the same order.",
        "sub": ""
      },
      "mela": {
        "title": "Chalo Mela Chalen",
        "local": "",
        "tag": "",
        "desc": "Child plans a path through simple visual mazes.",
        "sub": ""
      },
      "rachna": {
        "title": "Rachna",
        "local": "",
        "tag": "Construction skills",
        "desc": "Child builds shapes and patterns using pieces.",
        "sub": ""
      },
      "dhyan": {
        "title": "Dhyan Kahan Hai",
        "local": "",
        "tag": "",
        "desc": "Child responds only when they hear specific target words.",
        "sub": ""
      },
      "herpher": {
        "title": "Her Pher",
        "local": "",
        "tag": "",
        "desc": "Child keeps rules or information in mind while answering.",
        "sub": ""
      },
      "chor": {
        "title": "Chor Machaye Shor",
        "local": "",
        "tag": "Rule switching",
        "desc": "Child shifts between changing rules.",
        "sub": ""
      },
      "numeracy": {
        "title": "Ankganit",
        "local": "",
        "tag": "",
        "desc": "Age-appropriate tasks on counting and mathematics.",
        "sub": ""
      },
      "literacy": {
        "title": "Padh ke batao",
        "local": "",
        "tag": "",
        "desc": "Tasks around letters and early language readiness.",
        "sub": ""
      }
    }
  },
  "login": {
    "pill": "Child lookup for assessment",
    "heading": "Search a child",
    "headingSub": "and start the assessment",
    "description": "Use the unique Child ID to quickly bring up the child's details. Once you confirm the record, you can continue to the assessment.",
    "bullet1": "Enter the Child ID assigned at registration",
    "bullet2": "Verify name, gender, date of birth & mobile number",
    "bullet3": "Click 'Go to Assessment' to launch the test",
    "demoTip": "Demo tip: register a Child ID first before searching.",
    "fieldLabel": "Child ID",
    "placeholder": "Enter Child ID (e.g., CH001)",
    "searchBtn": "Search",
    "searchingBtn": "Searching...",
    "goToAssessment": "Go to Assessment",
    "cardTitle": "Child details",
    "noChild": "No child selected",
    "detailsFound": "Details found. Please verify before proceeding.",
    "labelName": "Name",
    "labelGender": "Gender",
    "labelDob": "Date of Birth",
    "labelMobile": "Mobile Number",
    "errRequired": "Please enter a Child ID.",
    "errNotFound": "Child ID not found. Please register the child first.",
    "errServer": "Error connecting to the server.",
    "errSession": "Could not start session. Please try again."
  },
  "register": {
    "pill": "Child registration module",
    "heading": "Register the child",
    "headingSub": "for Sangian assessment",
    "description": "Enter the details of the child whose cognitive assessment will be done using the Sangian test modules.",
    "bullet1": "Use the child's full official name wherever possible",
    "bullet2": "Confirm date of birth with records before starting",
    "bullet3": "Mobile number can be parent / guardian's primary contact",
    "photoLabel": "Child Photo",
    "childIdLabel": "Child Unique ID",
    "childIdPlaceholder": "Enter unique ID (e.g., CH001)",
    "nameLabel": "Child's Full Name",
    "namePlaceholder": "Enter child's full name",
    "fatherNameLabel": "Father Name",
    "fatherNamePlaceholder": "Enter father name",
    "motherNameLabel": "Mother Name",
    "motherNamePlaceholder": "Enter mother name",
    "dobLabel": "Date of Birth",
    "genderLabel": "Gender",
    "genderDefault": "Select gender",
    "genderFemale": "Female",
    "genderMale": "Male",
    "genderOther": "Other",
    "genderPrefer": "Prefer not to say",
    "mobileLabel": "Mobile Number",
    "mobilePlaceholder": "Parent / guardian 10-digit mobile number",
    "mobileHint": "Use a valid 10-digit mobile number (India).",
    "submitBtn": "Register Child",
    "submittingBtn": "Registering...",
    "successMsg": "Child registered successfully! Child ID: {id}",
    "errChildId": "Child Unique ID is required.",
    "errName": "Child's name is required.",
    "errDobRequired": "Date of Birth is required.",
    "errDobAge": "Child's age must be between 7 and 16 years.",
    "errGender": "Please select a gender.",
    "errMobileRequired": "Mobile number is required.",
    "errMobileInvalid": "Enter a valid 10-digit mobile number.",
    "errGeneral": "An error occurred during registration. Please try again."
  },
  "game": {
    "numeracyLabel": "Ankganit - V0",
    "childId": "Child ID",
    "score": "Score",
    "attemptLabel": "Attempt #",
    "pauseQuit": "Pause/Quit",
    "splashSubtitle": "Listen to the instructions, then start the assessment.",
    "welcome": "Welcome to Ankganit - V0",
    "splashDesc": "Please listen to the instructions. When the audio finishes, you can start the assessment.",
    "startNow": "Start Now",
    "replayAudio": "Replay",
    "audioNote": "The audio will play automatically. Once it ends, the Start Now button will become active.",
    "question": "Item",
    "of": "of",
    "category": "Category",
    "catSingle": "Single Number",
    "catDouble": "Double Number",
    "catSub": "Subtraction",
    "catDiv": "Division",
    "timer": "Timer",
    "correct": "Correct",
    "incorrect": "Incorrect",
    "quotient": "Quotient",
    "remainder": "Remainder",
    "answer": "Answer",
    "submitAnswer": "Submit Answer",
    "clear": "Clear",
    "assessmentComplete": "Assessment Report",
    "assessmentTerminated": "Assessment Terminated",
    "allQuestionsCompleted": "All questions completed",
    "finalResults": "Final Results",
    "welcomeBagiya": "Welcome to Bagiya",
    "welcomeLottery": "Welcome to Lottery Ka Ticket",
    "welcomeMela": "Welcome to Chalo Mela Chalen",
    "welcomeDhyan": "Welcome to Dhyan Kahan Hai",
    "welcomeHerPher": "Welcome to Her Pher - V0",
    "welcomeRachna": "Welcome to Rachna",
    "welcomeChor": "Welcome to Chor Machaye Shor",
    "notScored": "Not Scored",
    "practiceLabel": "Practice",
    "scoredLabel": "Scored",
    "replayBtn": "🔊 Replay Audio",
    "practiceQuestion": "Practice · Question",
    "practiceResponse": "Practice · Response",
    "tryAgain": "Try Again",
    "startMainGame": "Start Main Game",
    "responseLabel": "Response",
    "subQuestion": "Sub-question",
    "allAnswered": "All questions answered!",
    "nextQuestionArrow": "Next Question →",
    "nextScreenArrow": "Next Screen →",
    "finishGame": "Finish Game",
    "validationRequired": "This field is required. Please select an option.",
    "validationQ5": "Please select at least one observed behaviour.",
    "sampleQuestionA": "Sample Question A",
    "sampleQuestionLabel": "Sample Question",
    "listenInstructions": "Listen to the instructions carefully",
    "listenRemember": "Listen & Remember",
    "listeningLabel": "🔊 Listening…",
    "correctFeedback": "✅ Correct!",
    "incorrectFeedback": "❌ Not quite. Listen to the correct sequence again.",
    "sampleRound": "Sample Round",
    "timerLabel": "⏱ Timer:",
    "outstandingMemory": "Outstanding performance! You're a memory star! ⭐",
    "outstandingRachna": "Outstanding performance! 🌟",
    "sessionDropped": "Session Dropped (Clinical Rule)",
    "sessionTerminated": "Session Terminated",
    "assessmentCompleted2": "Assessment Completed",
    "retakeBtn": "↺ Retake",
    "replayShort": "↻ Replay",
    "stopBtn": "■ Stop",
    "doneBtn": "↻ Done",
    "targetPattern": "Target Pattern",
    "buildThis": "Build this",
    "workspaceLabel": "Workspace",
    "dropShapes": "Drop shapes here to build the pattern",
    "sourceShapes": "Source Shapes",
    "dragToWorkspace": "Drag to workspace",
    "correctFeedbackChor": "✔ Correct!",
    "incorrectFeedbackChor": "✖ Try again. Observe carefully.",
    "foundPattern": "✅ Great! You found the pattern",
    "phase1Complete": "Phase 1 Completed! Click Phase 2 to continue.",
    "trial2Label": "🔄 Trial 2",
    "totalMoves": "Total Moves",
    "detailedBreakdown": "Detailed Question Breakdown",
    "movesLabel": "Moves",
    "sNo": "S. No",
    "questionImage": "Question Image",
    "exactLabel": "✓ Correct (2pts)",
    "partialLabel": "~ Partial (1pt)",
    "wrongLabel": "✗ Incorrect (0pts)",
    "accuracyLabel": "% Accuracy",
    "timeLabel": "⏱ Time",
    "screensLabel": "Screens",
    "screenHeader": "Screen",
    "subQHeader": "Sub-Q",
    "targetHeader": "Target",
    "childChoseHeader": "Child Choose",
    "statusHeader": "Status",
    "ptsLabel": "pts",
    "statusCorrect": "Correct",
    "statusPartial": "Partial",
    "statusWrong": "Incorrect",
    "timeChip": "Screentime:",
    "totalCorrect": "Total / Correct",
    "correctMetric": "✓ Correct",
    "incorrectMetric": "✗ Incorrect",
    "totalTimeMetric": "Duration",
    "avgQMetric": "⚡ Avg/Q",
    "qNumHeader": "Q#",
    "yourResponse": "Your Response",
    "replaysHeader": "Replays",
    "outstandingLottery": "Outstanding performance! Great memory span! ⭐",
    "stoppedMsg": "Stopped after 3 consecutive incorrect answers",
    "testCompleted": "Test completed",
    "teachingLabel": "Teaching",
    "teachingQ1Label": "Teaching 1",
    "teachingQ2Label": "Teaching 2",
    "startMainQuestions": "Start Main Questions",
    "pleaseWaitAudio": "🔊 Please wait for the correction audio…",
    "sampleLabel": "Sample",
    "audioAutoPlay": "Audio plays automatically",
    "selectNumbers": "Select {n} numbers in order",
    "selectionComplete": "Selection complete",
    "questionsLabel": "Questions",
    "performanceMeter": "Performance Meter",
    "targetedMoves": "Targeted Moves:",
    "userMoves": "User Moves:",
    "timeTakenLabel": "Duration:",
    "userSelectedPath": "User Selected Path",
    "path1": "Path 1",
    "path2": "Path 2",
    "path3": "Path 3",
    "sampleALabel": "Sample A",
    "sampleBLabel": "Sample B",
    "teachingQ3Label": "Teaching 3",
    "teachingQ4Label": "Teaching 4",
    "trialLabel": "Trial",
    "refreshBtn": "Refresh",
    "currentTrialLabel": "CURRENT TRIAL",
    "movesInfoLabel": "MOVES",
    "timeInfoLabel": "TIME",
    "questionEndedMoves": "Question Ended Due to Illegal Moves",
    "timeoutDestination": "Time Out – Destination Not Achieved",
    "destinationNotAchieved": "Destination Not Achieved",
    "sessionTerminatedPartial": "Assessment Report",
    "droppedSubtitle": "Q1-Q3 score < 2: System-enforced drop",
    "assessorExit": "Assessor requested early exit",
    "testFinished": "Test finished successfully",
    "nextQuestion": "Next Question",
    "requiredStar": "* (Required)",
    "welcomeLiteracy": "Welcome to Padh ke batao - V0",
    "doneReading": "Done Reading",
    "assessorStopped": "Assessor stopped:",
    "questionNo": "Question No.",
    "questionType": "Question Type",
    "attemptStatus": "Attempt Status",
    "allCriteriaMet": "All Criteria Met",
    "attempted": "Attempted",
    "ssrQuestion": "Assessment Question",
    "userAnswer": "User Answer",
    "assessmentLabel": "Assessment Report",
    "yesLabel": "Yes",
    "noLabel": "No",
    "finishAssessment": "Finish Assessment",
    "droppedChip": "⛔ Dropped",
    "time": "Time",
    "performance": "Ankganit - V0 Performance",
    "performanceLabel": "Performance",
    "assessmentCompleted": "Assessment Completed",
    "totalScore": "Total Score",
    "correct2": "Correct",
    "incorrect2": "Incorrect",
    "percentage": "Accuracy",
    "totalTime": "Duration",
    "avgTimeQ": "Avg Time/Q",
    "outstanding": "Outstanding performance! You're a math star! ⭐",
    "showGrid": "Show per-question results with time",
    "reasonLabel": "Reason:",
    "sessionDetails": "Session Assessment Details",
    "assessorInstruction": "This section must be filled by the Assessor.",
    "q1Label": "Q1. Did you enjoy playing the game?",
    "q2Label": "Q2. How did the game feel for you?",
    "q3Label": "Q3. Did you feel tired while playing the game?",
    "q4Label": "Q4. Would you like to play the game again?",
    "optYes": "Yes, a lot",
    "optLittle": "A little",
    "optNotMuch": "Not much",
    "q5Label": "Q5. Observed Behaviours during the session (Multiple selection allowed)",
    "b1": "Difficulty sustaining attention",
    "b2": "Impulsive or random responding",
    "b3": "Negative reaction to correction",
    "b4": "Hesitation in responding",
    "b5": "High focus or persistence",
    "b6": "Verbalisation of a memory strategy",
    "b7": "Needed frequent reassurance",
    "b8": "Calm and engaged throughout",
    "extraNotes": "Additional Notes",
    "useMic": "Use Mic",
    "recordingStop": "Recording... (Stop)",
    "dictatePlaceholder": "Type or dictate observations...",
    "retest": "↻ Retest",
    "home": "🏠 Home",
    "submitAssessment": "Submit Assessment",
    "confirmSubmitMsg": "Are you sure you want to submit the assessment?",
    "confirm": "Confirm",
    "assessmentSubmitted": "Assessment submitted successfully!",
    "saving": "Saving...",
    "progressFound": "Saved Progress Found",
    "progressDesc": "You have a previously paused game session.",
    "restartFresh": "Restart Fresh",
    "resumeGame": "Resume Game",
    "pauseQuitTitle": "Pause or Quit",
    "pauseDesc": "Why are you stopping the game?",
    "pausePlaceholder": "E.g., Child is tired, disconnected, etc.",
    "cancel": "Cancel",
    "pauseSave": "Pause & Save",
    "quitEnd": "Quit & End",
    "carelessMistakePrompt": "Was this a careless mistake? Give one more try?",
    "chooseSubtractionQuestion": "Choose which problem to attempt first",
    "assessorRecordsSubtraction": "Ask the child to read the numbers, identify the sign, and solve on paper. Then record the result below.",
    "assessorRecordsDivision": "Ask the child to solve on paper, then enter what they wrote below.",
    "selectFiveNumbers": "Ask the child to choose any 5 numbers to identify",
    "subtractionQ1RetryInstruction": "You solved the second subtraction problem correctly. Now, please try the first subtraction problem once again. Take your time and check your work carefully before answering.",
    "scoreTable": {
      "questionNo": "Question No",
      "question": "Question",
      "correctAnswer": "Correct Answer",
      "score": "Score",
      "timeTaken": "Time Taken",
      "duration": "Duration",
      "status": "Status",
      "correct": "Correct",
      "incorrect": "Incorrect"
    },
    "categories": {
      "singleLetter": "Single Letter",
      "doubleLetter": "Double Letter",
      "sentence": "Sentence",
      "story": "Story",
      "paragraph": "Paragraph",
      "singleNumber": "Single Number",
      "doubleNumber": "Double Number",
      "subtraction": "Subtraction",
      "division": "Division"
    },
    "readingCriteria": {
      "skipWords": "Did the child skip any words?",
      "pronunciation": "Did the child make pronunciation errors?",
      "neededHelp": "Did the child need help reading?"
    },
    "reply": "Replay",
    "nextScreen": "Next Screen",
    "exposureLabel": "This is",
    "practiceQuestionLabel": "Practice Question",
    "submitResponse": "Submit",
    "notQuiteRight": "❌ Not quite right.",
    "lotteryAccuracyTooltip": "Correct Answers ÷ Total Questions (22) × 100",
    "restartTrial": "Restart Trial {n}?",
    "dictateReason": "Dictate Reason",
    "assessmentTitle": "Assessment",
    "triangleCriteria": {
      "q1": "The gap between two triangles is more than 2 squares.",
      "q2": "The alignment between the triangles has a difference of more than 2 squares.",
      "q3": "The shape created by the child matches the reference figure."
    }
  },
  "modal": {
    "disclaimer": "This description is informational only. Always follow your official KABC manual and institutional protocols when administering tests.",
    "close": "Close",
    "startGame": "Start Game",
    "noUrl": "Start URL is not configured for this test yet."
  },
  "help": {
    "statusOpen": "Open",
    "statusInProgress": "In Progress",
    "statusWaitingForReply": "Waiting for Reply",
    "statusResolved": "Resolved",
    "statusClosed": "Closed",
    "badge": "🎫 Help & Support",
    "title": "How can we help you?",
    "subtitle": "Our support team is here to assist you with any issues related to the Sangian Assessment Platform. Create a ticket and we'll respond within 1–2 business days.",
    "createCardTitle": "Create New Ticket",
    "createCardDesc": "Have an issue or question? Submit a support ticket and we'll get back to you as soon as possible.",
    "createCardItem1": "Email verification required",
    "createCardItem2": "Attach up to 3 screenshots",
    "createCardItem3": "Unique Ticket ID assigned",
    "createCardItem4": "Email confirmation sent",
    "createBtn": "Create Ticket →",
    "myCardTitle": "My Tickets",
    "myCardDesc": "Already submitted a ticket? Access your ticket history and continue the conversation with our team.",
    "myCardItem1": "View all your tickets",
    "myCardItem2": "Full conversation history",
    "myCardItem3": "Reply and add attachments",
    "myCardItem4": "Track ticket status live",
    "myTicketBtn": "View My Tickets →",
    "defaultFaqTitle": "Frequently Asked Questions",
    "otpTitle": "Verify Your Email",
    "otpSubPrefix": "We'll send a one-time code to confirm your email before you can",
    "otpPurposeCreate": "Create New Ticket",
    "otpPurposeView": "Access My Tickets",
    "otpEmailLabel": "Email Address",
    "otpEmailPlaceholder": "you@example.com",
    "otpSendBtn": "Send Verification Code",
    "otpSending": "Sending…",
    "otpSentTo": "Code sent to",
    "otpDevMode": "⚠️ Dev Mode — SMTP not configured",
    "otpAutoFill": "Auto-fill",
    "otpCodeLabel": "6-Digit Code",
    "otpCodePlaceholder": "• • • • • •",
    "otpVerifyBtn": "Verify & Continue",
    "otpVerifying": "Verifying…",
    "otpResendIn": "Resend OTP in {n}s",
    "otpResend": "Resend OTP",
    "otpErrEmail": "Please enter a valid email address.",
    "otpErrEnter": "Please enter the OTP.",
    "otpErrSend": "Failed to send OTP. Please try again.",
    "otpErrVerify": "Invalid OTP. Please try again.",
    "createTitle": "Create New Ticket",
    "verifiedAs": "Verified as",
    "fieldSubject": "Subject / Title",
    "fieldSubjectPlaceholder": "Brief description of your issue…",
    "fieldDesc": "Description",
    "fieldDescPlaceholder": "Describe your issue in detail — what happened, what you expected, and any steps to reproduce…",
    "fieldAttachments": "Attachments",
    "optional": "(optional)",
    "addImage": "📎 Add Image",
    "fileHint": "{cur}/{max} · JPG, PNG, WEBP · max 5 MB each",
    "submitBtn": "🎫 Submit Ticket",
    "submitting": "Submitting…",
    "back": "← Back",
    "errTitle": "Ticket title is required.",
    "errDesc": "Description is required.",
    "errCreate": "Failed to create ticket. Please try again.",
    "successTitle": "Ticket Submitted!",
    "successSub": "Your support ticket has been created. We'll get back to you within 1–2 business days.",
    "successIdLabel": "Your Ticket ID",
    "successNote": "Keep this ID handy — you'll need it to track your ticket.",
    "viewMyTickets": "View My Tickets",
    "createAnother": "Create Another Ticket",
    "myTicketsTitle": "My Tickets",
    "viewingFor": "Viewing tickets for",
    "noTickets": "No tickets found for this email address.",
    "createdOn": "Created",
    "unreadNew": "new",
    "errLoad": "Failed to load tickets.",
    "backToTickets": "← My Tickets",
    "openedOn": "Opened",
    "you": "👤 You",
    "supportTeam": "🛡️ Support Team",
    "closedNote": "This ticket is closed. Please create a new ticket if you need further assistance.",
    "replyPlaceholder": "Write your reply…",
    "sendReply": "📤 Send Reply",
    "sending": "Sending…",
    "errReply": "Please type a message before sending.",
    "errSendReply": "Failed to send reply.",
    "errLoadTicket": "Failed to load ticket."
  },
  "contact": {
    "docTitle": "Contact Us | Community Empowerment Lab",
    "badge": "📬 Get in Touch",
    "title": "Contact Us",
    "subtitle": "We're here to help and would love to hear from you. Whether you have a question, feedback, or need support, feel free to reach out through the form below or any of our contact channels.",
    "infoEmail": "EMAIL",
    "infoPhone": "PHONE",
    "formTitle": "Send Us a Message",
    "formSub": "Fill in the form and we'll get back to you shortly.",
    "fName": "Full Name",
    "fNamePH": "Your full name",
    "fEmail": "Email Address",
    "fEmailPH": "you@example.com",
    "fPhone": "Phone Number",
    "fPhonePH": "+91 00000 00000",
    "fSubject": "Subject",
    "fSubjectPH": "How can we help?",
    "fMessage": "Message",
    "fMessagePH": "Describe your query or message in detail…",
    "sendBtn": "🚀 Send Message",
    "sendingBtn": "⏳ Sending…",
    "eReqName": "Full name is required.",
    "eReqEmail": "Email address is required.",
    "eBadEmail": "Enter a valid email address.",
    "eReqPhone": "Phone number is required.",
    "eReqSubject": "Subject is required.",
    "eReqMessage": "Message is required.",
    "eMaxMessage": "Message must be under 5000 characters.",
    "eFailSend": "Failed to send. Please try again.",
    "successTitle": "Thank You for Reaching Out!",
    "successBody": "Your message has been successfully submitted to the Community Empowerment Lab team. We typically respond within 1–2 business days via the email address you provided.",
    "successNote": "In the meantime, feel free to explore our assessment platform or check our",
    "successLink": "Help & Support",
    "successNoteEnd": "section for quick answers.",
    "sendAnother": "Send Another Message"
  }
};
